import { PaStatus } from "../../../../enums/paStatus";
import {
  PaStatusBucket,
  PaStatusBucketColorCategory,
  PaStatusBucketColorMapping,
  PaStatusBucketLabels,
} from "../../../../enums/paStatusBucket";

interface PaStatusBucketBadgeStyles {
  text: string;
  textColor: string;
  bgColor: string;
  hoverText: string;
}

// Map display labels to enum values for reverse lookup
const labelToEnumMap: Record<string, PaStatusBucket> = Object.entries(
  PaStatusBucketLabels,
).reduce(
  (acc, [enumValue, label]) => {
    // Store with normalized lowercase key for case-insensitive matching
    acc[label.toLowerCase()] = enumValue as PaStatusBucket;
    return acc;
  },
  {} as Record<string, PaStatusBucket>,
);

/**
 * Finds the matching PaStatusBucket enum value from a pa_status_reason string
 * Supports both exact label matches and enum value matches
 */
export const findPaStatusBucket = (
  paStatusReason: string | null | undefined,
): PaStatusBucket | null => {
  if (!paStatusReason) return null;

  const normalizedReason = paStatusReason.toLowerCase().trim();

  // Check if it matches a label (case-insensitive)
  if (labelToEnumMap[normalizedReason]) {
    return labelToEnumMap[normalizedReason];
  }

  // Check if it matches an enum value directly
  const enumValues = Object.values(PaStatusBucket);
  const matchedEnum = enumValues.find(
    (enumVal) => enumVal.toLowerCase() === normalizedReason,
  );

  if (matchedEnum) {
    return matchedEnum;
  }

  // Partial matching for common variations
  const partialMatches: Record<string, PaStatusBucket> = {
    approval: PaStatusBucket.APPROVAL_ON_FILE,
    approved: PaStatusBucket.APPROVAL_ON_FILE,
    denial: PaStatusBucket.DENIAL_ON_FILE,
    denied: PaStatusBucket.DENIAL_ON_FILE,
    "not covered": PaStatusBucket.NOT_COVERED_PLAN_EXCLUSION,
    "plan exclusion": PaStatusBucket.NOT_COVERED_PLAN_EXCLUSION,
    "medically unnecessary": PaStatusBucket.MEDICALLY_UNNECESSARY,
    "step therapy": PaStatusBucket.STEP_THERAPY_REQUIRED,
    onshore: PaStatusBucket.DONE_BY_ONSHORE,
    "client assistance": PaStatusBucket.ASKED_CLIENT_ASSISTANCE,
    expired: PaStatusBucket.EXPIRED,
    "processor error": PaStatusBucket.PROCESSOR_ERROR,
    "call required": PaStatusBucket.UNABLE_TO_PROCESS_CALL_REQUIRED,
    "unable to process": PaStatusBucket.UNABLE_TO_PROCESS_CALL_REQUIRED,
    cancelled: PaStatusBucket.CANCELLED_CLOSED_DUPLICATE,
    closed: PaStatusBucket.CANCELLED_CLOSED_DUPLICATE,
    duplicate: PaStatusBucket.CANCELLED_CLOSED_DUPLICATE,
    "eligibility error": PaStatusBucket.ELIGIBILITY_ERROR,
    prerequisite: PaStatusBucket.PROGRAM_PREREQUISITE,
    "no review": PaStatusBucket.NO_REVIEW_NEEDED,
    "benefit limitation": PaStatusBucket.BENEFIT_LIMITATION,
    "system failure": PaStatusBucket.UNSPECIFIED_SYSTEM_FAILURE,
    "no predefined": PaStatusBucket.NO_PREDEFINED_BUCKETS,
  };

  for (const [key, bucket] of Object.entries(partialMatches)) {
    if (normalizedReason.includes(key)) {
      return bucket;
    }
  }

  return null;
};

/**
 * Returns the PA outcome classification to display in the table.
 * Approved rows always show an empty bucket regardless of stored classification.
 *
 * @input responseStatus - Row `response_status` value
 * @input paOutcomeClassification - Raw `pa_outcome_classification` from the API
 * @return null when status is Approved, otherwise the classification value
 */
export const resolveDisplayPaOutcomeClassification = (
  responseStatus: string | null | undefined,
  paOutcomeClassification: string | null | undefined,
): string | null | undefined => {
  if (responseStatus?.toLowerCase() === PaStatus.APPROVED.toLowerCase()) {
    return null;
  }

  return paOutcomeClassification ?? null;
};

/**
 * Gets the badge styles (text color, background color) for a PA Status Bucket
 * based on the pa_status_reason value from the data
 */
export const getPaStatusBucketBadgeStyles = (
  paStatusReason: string | null | undefined,
): PaStatusBucketBadgeStyles => {
  const GREY_BADGE_BG = "#F5F5F5";
  const BLACK_TEXT = "#000000";

  const defaultStyles: PaStatusBucketBadgeStyles = {
    text: paStatusReason || "--",
    textColor: BLACK_TEXT,
    bgColor: GREY_BADGE_BG,
    hoverText: paStatusReason || "--",
  };

  if (!paStatusReason || paStatusReason === "--") {
    return defaultStyles;
  }

  const bucket = findPaStatusBucket(paStatusReason);

  if (!bucket) {
    // If no match found, return default styles with original text
    return defaultStyles;
  }

  const label = PaStatusBucketLabels[bucket];

  return {
    text: label,
    textColor: BLACK_TEXT,
    bgColor: GREY_BADGE_BG,
    hoverText: label,
  };
};

/**
 * Gets the color category for a PA Status Bucket
 * Useful for conditional styling or logic based on status type
 */
export const getPaStatusBucketColorCategory = (
  paStatusReason: string | null | undefined,
): PaStatusBucketColorCategory | null => {
  if (!paStatusReason) return null;

  const bucket = findPaStatusBucket(paStatusReason);
  if (!bucket) return null;

  return PaStatusBucketColorMapping[bucket];
};

/**
 * Checks if a PA Status Bucket represents a positive outcome (approved)
 */
export const isPaStatusBucketPositive = (
  paStatusReason: string | null | undefined,
): boolean => {
  const category = getPaStatusBucketColorCategory(paStatusReason);
  return category === PaStatusBucketColorCategory.SUCCESS;
};

/**
 * Checks if a PA Status Bucket represents a negative outcome (denied)
 */
export const isPaStatusBucketNegative = (
  paStatusReason: string | null | undefined,
): boolean => {
  const category = getPaStatusBucketColorCategory(paStatusReason);
  return (
    category === PaStatusBucketColorCategory.DANGER ||
    category === PaStatusBucketColorCategory.ERROR
  );
};

/**
 * Checks if a PA Status Bucket requires action
 */
export const isPaStatusBucketRequiresAction = (
  paStatusReason: string | null | undefined,
): boolean => {
  const category = getPaStatusBucketColorCategory(paStatusReason);
  return category === PaStatusBucketColorCategory.WARNING;
};
